import React, { FC, useRef, useState } from 'react';
import styled from 'styled-components';
import { ArticleWithButton } from './ArticleWithButton';
import { GreySummary } from './GreySummary';
import { InitialOpenReport } from './InitialOpenReport';
import { CustomBlockContent } from './CustomBlockContent';

interface InitialFoldedReportProps {
  data: any;
}

export const InitialFoldedReport: FC<InitialFoldedReportProps> = ({ data }) => {
  const {
    title,
    secondTitle,
    _rawMainContent,
    prefaceTitle,
    _rawPrefaceContent,
    _rawSummaryGreyContentAfterDivider,
    _rawSummaryGreyContent,
    _rawSummaryAfterGreyContent,
    summaryTitle,
    _rawSummaryAfterImage,
    summaryImage,
    backgroundImage,
    pdf,
    opacity,
    _rawTextTwo,
    _rawTextThree,
    _rawTextFour,
    imageTwo,
    imageThree,
    imageFour,
  } = data;

  const [open, setOpen] = useState(true);
  const preface = useRef<null | HTMLDivElement>(null);
  const scrollToGallery: any = (): any => {
    preface.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleExecutiveSummary = () => {
    setOpen(!open);
  };

  const buttonText = open ? 'Close executive summary' : 'Executive summary';
  const opacityDecimals = opacity ? opacity / 10 : 0.5;
  const opacityBackground = `rgba(0,0,0, ${opacityDecimals})`;

  return (
    <>
      <StyledInitialFoldedReport
        opacityBackground={opacityBackground}
        imageUrl={backgroundImage.asset.url}
      >
        <div>
          <div className="background">
            <div className="box-center">
              <div className="content">
                <h1 className="main-title">{title}</h1>
                <h3 className="second-title">{secondTitle}</h3>
                <CustomBlockContent blocks={_rawMainContent} />
                <div className="button-container">
                  <button
                    className="expand-button red active "
                    onClick={scrollToGallery}
                  >
                    Learn more
                  </button>
                  <button className="download-button">
                    <a
                      className="download-button"
                      href={`${pdf.asset.url}?dl=`}
                    >
                      Download the pdf
                    </a>
                  </button>
                </div>
              </div>
            </div>
            <div className="s9-widget-wrapper"></div>
          </div>
        </div>
      </StyledInitialFoldedReport>
      <ArticleWithButton
        title={prefaceTitle}
        article={_rawPrefaceContent}
        toggle={toggleExecutiveSummary}
        scrollToRef={preface}
        buttonText={buttonText}
        open={open}
      />
      <GreySummary open={open}>
        <InitialOpenReport
          toggle={toggleExecutiveSummary}
          title={summaryTitle}
          greyContent={_rawSummaryGreyContent}
          _rawSummaryAfterGreyContent={_rawSummaryAfterGreyContent}
          _rawSummaryGreyContentAfterDivider={
            _rawSummaryGreyContentAfterDivider
          }
          _rawSummaryAfterImage={_rawSummaryAfterImage}
          summaryImage={summaryImage}
          _rawTextTwo={_rawTextTwo}
          _rawTextThree={_rawTextThree}
          _rawTextFour={_rawTextFour}
          imageTwo={imageTwo}
          imageThree={imageThree}
          imageFour={imageFour}
        />
      </GreySummary>
    </>
  );
};

const StyledInitialFoldedReport = styled.div<any>`
  .s9-widget-wrapper {
    z-index: 2;
    width: 100%;
    display: flex;
    justify-content: center;
    .s9-btn-share {
      width: 100px;
      @media (max-width: 641px) {
        width: auto;
      }
    }
  }

  .background {
    background-image: url(${(p) => p.imageUrl});
    position: relative;
    height: 100vh;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    align-content: center;

    &:before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      background: ${(p) => p.opacityBackground};
    }

    .box-center {
      z-index: 10;
      display: flex;
      width: 50%;
      align-items: center;
      justify-content: center;
      padding: 65px 50px;
      background-color: rgba(249, 249, 249, 0.9);
      margin-bottom: 10px;
      @media (max-width: 1400px) {
        width: 70%;
      }

      @media (max-width: 768px) {
        width: 60%;
      }

      @media (max-width: 500px) {
        width: 80%;
        padding: 40px 20px 20px 20px;
      }

      .content {
        display: flex;
        flex-wrap: wrap;
        text-align: center;
        justify-content: center;

        div {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
        }

        h1 {
          margin-bottom: 0px;
        }

        .second-title {
          min-width: 100%;
          font-size: 2em;
          text-align: center;

          @media (max-width: 768px) {
            font-size: 1em;
          }
        }

        p {
          font-size: 1em;
          font-style: italic;
          margin: 20px 0 35px 0;
          color: var(--black);
          width: 80%;
          min-width: 80%;
          @media (max-width: 768px) {
            font-size: 0.8em;
            width: 100%;
            margin: 20px 0 20px 0;
          }
        }

        .button-container {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          width: 320px;

          @media (min-width: 2000px) {
            min-width: 21%;
          }

          @media (max-width: 768px) {
            font-size: 0.8em;
            width: 180px;
          }

          .download-button {
            background: transparent;
            color: var(--red);
            box-shadow: 0;
          }
        }
      }
    }
  }
`;
